import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Search,
  Download,
  RefreshCw,
  AlertCircle,
  Info,
  AlertTriangle,
  CheckCircle,
  Bug,
  Loader2,
} from 'lucide-react'
import { cn, getEnv } from '@/lib/utils'
import { gatewayFetch } from '@/lib/gateway-auth'

interface LogEntry {
  id: string
  timestamp: string
  level: 'info' | 'warning' | 'error' | 'success' | 'debug'
  source: string
  message: string
}

function getLogsUrl(): string {
  const base = getEnv('VITE_GATEWAY_URL').replace(/\/$/, '')
  return base ? `${base}/system/logs` : '/api/system/logs'
}

const levelIcons: Record<string, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  error: AlertCircle,
  success: CheckCircle,
  debug: Bug,
}

const levelColors: Record<string, string> = {
  info: 'text-sky-400',
  warning: 'text-amber-400',
  error: 'text-rose-400',
  success: 'text-emerald-400',
  debug: 'text-zinc-400',
}

const levelBadges: Record<string, string> = {
  info: 'border-sky-500/30 bg-sky-500/10 text-sky-300',
  warning: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  error: 'border-rose-500/30 bg-rose-500/10 text-rose-300',
  success: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  debug: 'border-zinc-500/30 bg-zinc-500/10 text-zinc-300',
}

export default function LogsPage() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<string>('all')
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(true)

  const fetchLogs = useCallback(async () => {
    try {
      const url = new URL(getLogsUrl(), window.location.origin)
      url.searchParams.set('limit', '200')
      const res = await gatewayFetch(url.toString())
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setLogs(data.logs || [])
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  useEffect(() => {
    if (!autoRefresh) return
    const id = setInterval(fetchLogs, 5000)
    return () => clearInterval(id)
  }, [autoRefresh, fetchLogs])

  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      log.message.toLowerCase().includes(search.toLowerCase()) ||
      log.source.toLowerCase().includes(search.toLowerCase())
    const matchesFilter = filter === 'all' || log.level === filter
    return matchesSearch && matchesFilter
  })

  const handleExport = () => {
    const text = filteredLogs
      .map((l) => `[${l.timestamp}] [${l.level.toUpperCase()}] [${l.source}] ${l.message}`)
      .join('\n')
    const blob = new Blob([text], { type: 'text/plain' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `omtae-logs-${new Date().toISOString().split('T')[0]}.txt`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const formatTimestamp = (ts: string) => {
    try {
      return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    } catch {
      return ts
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">System Logs</h1>
          <p className="text-muted-foreground mt-1">
            Live gateway logs {autoRefresh && <span className="text-xs">(auto-refreshing)</span>}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant={autoRefresh ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAutoRefresh((v) => !v)}
          >
            {autoRefresh ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {autoRefresh ? 'Live' : 'Paused'}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchLogs}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={filteredLogs.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-rose-500/30 bg-rose-500/5">
          <CardContent className="p-4 text-sm text-rose-300">
            Failed to fetch logs: {error}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search logs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="debug">Debug</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {loading ? 'Loading...' : `Recent Logs (${filteredLogs.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            {filteredLogs.length === 0 && !loading ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Info className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">
                  {logs.length === 0
                    ? 'No logs yet. Logs appear as the gateway processes requests.'
                    : 'No logs match your filter.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredLogs.map((log) => {
                  const Icon = levelIcons[log.level] || Info
                  return (
                    <div
                      key={log.id}
                      className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted transition-colors"
                    >
                      <Icon className={cn('h-5 w-5 mt-0.5', levelColors[log.level])} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-muted-foreground font-mono">
                            {formatTimestamp(log.timestamp)}
                          </span>
                          <Badge
                            variant="secondary"
                            className={cn('text-xs', levelBadges[log.level])}
                          >
                            {log.level}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {log.source}
                          </Badge>
                        </div>
                        <p className="text-sm mt-1">{log.message}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Level Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {(['info', 'success', 'warning', 'error', 'debug'] as const).map((level) => {
          const Icon = levelIcons[level]
          return (
            <Card key={level}>
              <CardContent className="p-4">
                <div className="flex items-center space-x-2">
                  <Icon className={cn('h-4 w-4', levelColors[level])} />
                  <span className="text-sm font-medium capitalize">{level === 'warning' ? 'Warnings' : level === 'error' ? 'Errors' : level}</span>
                </div>
                <p className="text-2xl font-bold mt-2">
                  {logs.filter((l) => l.level === level).length}
                </p>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
